import type { TFunction } from 'i18next';
import { z } from 'zod';

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
const UPI_RE = /^[\w.-]+@[\w.-]+$/;
const ACCOUNT_RE = /^\d{9,18}$/;

export function createCancelOrReturnFormSchema(t: TFunction<'orders'>, requirePayout: boolean) {
  return z
    .object({
      reasonCode: z.string().min(1, t('cancelReturn.reasonRequired')),
      reasonNote: z.string(),
      accountHolderName: z.string(),
      accountNumber: z.string(),
      ifscCode: z.string(),
      upiId: z.string(),
    })
    .superRefine((val, ctx) => {
      if (val.reasonCode === 'other' && !val.reasonNote.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('cancelReturn.otherReasonRequired'),
          path: ['reasonNote'],
        });
      }

      if (!requirePayout) return;

      const holder = val.accountHolderName.trim();
      const account = val.accountNumber.trim();
      const ifsc = val.ifscCode.trim();
      const upi = val.upiId.trim();

      if (!holder) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('cancelReturn.payout.accountHolderRequired'),
          path: ['accountHolderName'],
        });
      }

      const hasBank = account.length > 0 && ifsc.length > 0;
      const hasUpi = upi.length > 0;

      if (!hasBank && !hasUpi) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('cancelReturn.payout.bankOrUpiRequired'),
          path: ['upiId'],
        });
      }

      if (account && !ACCOUNT_RE.test(account)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('cancelReturn.payout.accountNumberInvalid'),
          path: ['accountNumber'],
        });
      }

      if (ifsc && !IFSC_RE.test(ifsc)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('cancelReturn.payout.ifscInvalid'),
          path: ['ifscCode'],
        });
      }

      if (upi && !UPI_RE.test(upi)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('cancelReturn.payout.upiInvalid'),
          path: ['upiId'],
        });
      }
    });
}

export type CancelOrReturnFormValues = z.infer<ReturnType<typeof createCancelOrReturnFormSchema>>;
